<template>
  <model-form
    ref="modelForm"
    :model="order"
    data-test-id="order-form"
  >
    <div class="row q-gutter-md justify-end q-mb-md">
      <q-btn
        v-if="order.state === 'open_tab' && order.remainingOwed === 0"
        size="sm"
        label="Close Order"
        color="negative"
        outline
        :loading="closingOrder"
        :disable="!$isPermitted($permissions.Customer.Manage)"
        @click="closeOrder"
      />
      <q-btn
        v-if="order.state === 'completed'"
        size="sm"
        label="Print Items"
        outline
        :loading="printing"
        @click="() => { printEmailView = 'print'; handleItems(); }"
      />
      <q-btn
        v-if="order.state === 'completed'"
        size="sm"
        label="Email Items"
        outline
        :loading="emailing"
        :disable="!$isPermitted($permissions.Customer.Manage)"
        @click="() => { printEmailView = 'email'; handleItems(); }"
      />
      <div
        v-if="!availablePrintTemplates || availablePrintTemplates.length === 1"
        class="row q-gutter-x-md justify-end q-ml-none"
      >
        <q-btn
          v-if="order.state !== 'draft'"
          size="sm"
          label="Print Receipt"
          outline
          :loading="printing"
          @click="printReceipt"
        />
        <q-btn
          v-if="order.state !== 'draft'"
          size="sm"
          label="Email Receipt"
          outline
          :loading="emailing"
          :disable="!$isPermitted($permissions.Customer.Manage)"
          @click="() => { printEmailView = 'email'; handleReceipt(); }"
        />
        <q-btn
          v-if="currentSite.enableSmsReceiptsForQrCodeOrders && order.state !== 'draft'"
          size="sm"
          label="Send SMS Receipt"
          outline
          @click="sendSmsReceipt"
        />
        <q-btn
          size="sm"
          label="View Receipt"
          outline
          @click="$refs.receiptDialog.show(order.id)"
        />
      </div>
      <div
        v-else
        class="row q-gutter-x-md justify-end q-ml-none"
      >
        <q-btn
          size="sm"
          label="Print Order"
          outline
          :loading="printing"
          @click="printOrder"
        />
        <q-btn
          size="sm"
          label="Email Order"
          outline
          :loading="emailing"
          :disable="!$isPermitted($permissions.Customer.Manage)"
          @click="emailOrder"
        />
        <q-btn
          size="sm"
          label="View Order"
          outline
          @click="viewOrder"
        />
      </div>
    </div>
    <q-card
      flat
      bordered
      class="q-mb-lg"
    >
      <q-card-section
        class="row items-center q-gutter-x-sm q-mx-xs"
        data-test-id="order-form-summary"
      >
        <div class="q-table__title q-ml-none">
          Summary
        </div>
        <q-chip
          v-if="order.name"
          class="text-uppercase text-bold text-grey-9 q-mb-none sensitive-data-inside"
          :label="order.name"
          square
          dense
        />
        <q-btn
          :icon="!order.hasIssue ? 'check' : 'error'"
          :color="!order.hasIssue ? 'positive' : 'negative'"
          size="md"
          dense
          round
          flat
          :loading="runningAuditChecks"
          @mouseover="showAuditCheckMenu = true; showCorrectionsMenu = false"
          @click="runAuditChecks()"
        >
          <q-tooltip
            anchor="top middle"
            self="bottom middle"
          >
            Click to Rerun Audit Checks
          </q-tooltip>
          <q-menu
            v-model="showAuditCheckMenu"
            :offset="[0, 10]"
            @mouseleave="showAuditCheckMenu = false"
          >
            <q-list>
              <q-item
                v-for="check in order.auditChecks"
                :key="check.name"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="check.passed ? 'check' : 'error'"
                    :color="check.passed ? 'positive' : 'negative'"
                  />
                </q-item-section>
                <q-item-section>
                  {{ $_.startCase(check.name) }}
                </q-item-section>
                <q-item-section
                  v-if="check.description"
                  avatar
                >
                  <q-icon
                    name="info"
                  >
                    <q-tooltip>
                      {{ check.description }}
                    </q-tooltip>
                  </q-icon>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn
          v-if="order.countOfMismatchedEffectiveDates > 0"
          icon="today"
          size="md"
          dense
          round
          outline
          @click="showReconciliationDialog = true"
        >
          <q-badge
            color="red"
            floating
          >
            {{ order.countOfMismatchedEffectiveDates }}
          </q-badge>
          <q-tooltip
            anchor="top middle"
            self="bottom middle"
          >
            View Reconciliation
          </q-tooltip>
        </q-btn>
        <q-btn
          v-if="currentUser.admin && order.availableCorrections && order.availableCorrections.length > 0"
          icon="build"
          size="md"
          dense
          round
          flat
          :disable="!$isPermitted($permissions.Customer.Manage)"
          @mouseover="showCorrectionsMenu = true; showAuditCheckMenu = false"
          @click="showCorrectionsMenu = !showCorrectionsMenu"
        >
          <q-tooltip
            anchor="top middle"
            self="bottom middle"
          >
            Run Auto Correction
          </q-tooltip>
          <q-menu
            v-model="showCorrectionsMenu"
            :offset="[0, 10]"
            @mouseleave="showCorrectionsMenu = false"
          >
            <q-list>
              <q-item
                v-for="correction in order.availableCorrections"
                :key="correction"
                clickable
                @click="order.runCorrection(correction)"
              >
                <q-item-section>
                  {{ $_.startCase(correction) }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-space />
        <q-btn
          icon="description"
          :color="order.notes ? 'warning' : 'grey-8'"
          size="md"
          dense
          round
          flat
          @click="showNotesDialog = true"
        />
      </q-card-section>
      <q-card-section>
        <div class="row q-gutter-md">
          <q-input
            :model-value="$_.startCase(order.state)"
            class="col"
            label="State"
            readonly
          />
          <q-btn
            v-if="order.user"
            class="col sensitive-button"
            color="primary"
            outline
            :label="order.user.name"
            @click="$editModel(order.user)"
          />
          <q-input
            v-model="order.total"
            class="col"
            label="Total"
            :prefix="currentCircuit.localeCurrencySymbol"
            readonly
          />
          <q-input
            v-if="order.tip !== 0"
            v-model="order.tip"
            class="col"
            label="Tip"
            :prefix="currentCircuit.localeCurrencySymbol"
            readonly
          />
          <q-input
            v-if="!order.remoteOrderApplicationName"
            :model-value="order.registerStation && order.registerStation.name"
            class="col"
            label="Register"
            readonly
          />
          <q-input
            v-else
            :model-value="order.remoteOrderApplicationName"
            class="col"
            label="Remote Application"
            readonly
          />
          <q-input
            :model-value="order.employee && order.employee.name"
            class="col"
            label="Employee"
            readonly
          />
          <q-input
            :model-value="order.updatedAt ? order.updatedAt.i18n('timestamp') : null"
            class="col"
            label="Time"
            readonly
          />
        </div>
        <div class="row q-col-gutter-md q-mt-md">
          <q-input
            v-if="!order.user && order.deliveryEmailAddress"
            :model-value="order.deliveryEmailAddress"
            class="col-3 sensitive-input"
            label="User Email Address"
            readonly
          />
          <q-input
            v-if="!order.user && order.deliveryFirstName"
            :model-value="order.deliveryFirstName"
            class="col-3 sensitive-input"
            label="User First Name"
            readonly
          />
          <q-input
            v-if="!order.user && order.deliveryFirstName"
            :model-value="order.deliveryLastName"
            class="col-3 sensitive-input"
            label="User Last Name"
            readonly
          />
          <q-input
            v-if="!order.user && order.deliveryPhone"
            :model-value="order.deliveryPhone"
            class="col-3 sensitive-input"
            label="User Phone Number"
            readonly
          />
        </div>
      </q-card-section>
    </q-card>
    <model-table
      :collection="order.orderItems"
      :submodel="true"
      :columns="[
        { field: 'id' },
        { field: 'type', component: OrderItemTypeCell },
        { field: 'state', component: OrderStateCell },
        { field: 'description' },
        { field: 'price', component: 'money' },
        { field: 'discountAmount', component: 'money' },
        { field: 'voucherAmount', component: 'money' },
        { field: 'redemptionTotal', component: 'money' },
        { field: 'taxAmount', label: $t('pricing.tax') + ' Amount', component: 'money' },
        { field: 'total', component: 'money' },
        { field: 'pointsUsed' },
        { field: 'pointsEarned' },
        { field: 'deliveryType' },
        { field: 'details', component: OrderItemDetails },
        { field: 'collectedUser', component: 'model', classes: 'sensitive-column' },
        { name: 'collectedAt', field: row => row.collectedAt ? row.collectedAt.i18n('timestamp') : null, component: 'date-time' },
        { name: 'placedAt', field: row => row.placedAt ? row.placedAt.i18n('timestamp') : null, component: 'date-time' },
        { name: 'voidedAt', field: row => row.voidedAt ? row.voidedAt.i18n('timestamp') : null, component: 'date-time' },
      ]"
      :show-add="false"
      :cell-class="props => classesForItem(props.row)"
    >
      <template #header-buttons>
        <template v-if="order.canVoid && (order.state === 'completed' || order.state === 'partially_voided' || order.state === 'partially_canceled')">
          <div>
            <q-btn
              v-if="order.hasCollectableItems"
              label="Collect Items"
              color="primary"
              outline
              @click="$refs.collectionDialog.show(order)"
            />
          </div>
        </template>
        <template v-if="order.canVoid && (order.state === 'completed' || order.state === 'partially_voided' || order.state === 'partially_canceled')">
          <div>
            <q-btn
              label="Swap Items"
              outline
              :disable="!$register"
              @click="swap()"
            />
            <q-tooltip v-if="!$register">
              Only available in Register App
            </q-tooltip>
          </div>
        </template>
        <div>
          <q-btn
            v-if="(order.canVoid || order.isDisputed) && (order.state === 'completed' || order.state === 'partially_voided' || order.state === 'partially_canceled')"
            label="Refund Items"
            outline
            :disable="!order.canVoid"
            data-test-id="order-form-refund-button"
            @click="refund()"
          />
          <q-tooltip
            v-if="order.isDisputed"
          >
            {{ order.isDisputed ? 'Cannot issue refund with dispute in progress. ' : '' }}
          </q-tooltip>
        </div>
        <q-btn
          v-if="order.canVoid && order.state === 'open_tab'"
          label="Void Items"
          color="negative"
          outline
          @click="refund"
        />
        <q-btn
          v-if="order.state === 'open_tab'"
          label="Merge Tab"
          color="warning"
          outline
          @click="merge"
        />
        <q-btn
          v-if="order.canCancel"
          label="Cancel"
          color="negative"
          :loading="cancelingOrder"
          @click="cancelOrder()"
        />
      </template>
      <order-item-form :order="order" />
    </model-table>
    <div class="q-py-lg" />
    <model-table
      :collection="order.payments"
      :submodel="true"
      :columns="[
        { field: 'type', component: OrderItemTypeCell },
        { field: 'state', component: OrderStateCell },
        { name: 'details', field: payment => payment.details },
        { field: 'stripePaymentIntentId', label: ' ' },
        { field: 'stripeFailureMessage', label: ' ' },
        { field: 'amount', component: 'money' },
        { name: 'Time', field: row => row.paidAt ? row.paidAt.i18n('timestamp') : null, component: 'date-time' },
      ]"
      :show-add="false"
      :show-edit="false"
      :cell-class="props => classesForPayment(props.row)"
    >
      <template #header-buttons>
        <q-btn
          icon="today"
          size="md"
          dense
          round
          outline
          @click="showReconciliationDialog = true"
        >
          <q-badge
            v-if="order.countOfMismatchedEffectiveDates > 0"
            color="red"
            floating
          >
            {{ order.countOfMismatchedEffectiveDates }}
          </q-badge>
        </q-btn>
      </template>
      <payment-form />
    </model-table>
    <refund-dialog
      ref="refundDialog"
      :order="order"
      @refund-success="$emit('change')"
      @show-payment-dialog="(order) => $emit('select-order', order)"
    />
    <print-items-dialog
      ref="printItemsDialog"
    />
    <send-sms-receipt-dialog
      ref="sendSmsReceiptDialog"
    />
    <email-items-dialog
      ref="emailItemsDialog"
    />
    <collection-dialog
      ref="collectionDialog"
      :order="order"
    />
    <swap-dialog
      ref="swapDialog"
      :order="order"
      @next="order => $emit('select-order', order)"
    />
    <open-tab-merge-dialog
      ref="openTabMergeDialog"
      :order="order"
      @merge-order="(order) => mergeOrder(order)"
    />
    <options-dialog
      ref="optionsDialog"
    />
    <receipt-dialog
      ref="receiptDialog"
      v-model="showReceipt"
      :order-id="order.id"
    />
    <invoice-dialog
      ref="invoiceDialog"
      v-model="showInvoice"
      :order-id="order.id"
    />
    <custom-template-dialog
      ref="customTemplateDialog"
      v-model="showCustomTemplate"
      :order-id="order.id"
    />
    <q-dialog v-model="showEmailDialog">
      <q-card :style="{ 'width': '450px', 'max-width': '95%' }">
        <q-card-section
          class="text-h5"
        >
          Email Address
        </q-card-section>

        <q-card-section class="row justify-evenly items-center">
          <q-input
            v-model.trim="emailAddress"
            class="col"
            type="email"
            :label="$t('form.labels.emailAddress')"
            bottom-slots
            :rules="[
              val => required.$validator(val) || 'Field is required',
              val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
            ]"
            lazy-rules
          />
        </q-card-section>
        <q-card-actions class="row justify-end">
          <q-btn
            label="Send"
            color="primary"
            :loading="emailing"
            @click="emailCallback"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <notes-dialog
      v-model="showNotesDialog"
      :model="order"
    />
    <order-reconciliation-dialog
      v-model="showReconciliationDialog"
      :order="order"
    />
  </model-form>
</template>

<script>

import { email, required } from '@vuelidate/validators'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ModelTable from 'src/modules/admin/components/ModelTable'
import NotesDialog from 'src/modules/admin/components/NotesDialog'
import CollectionDialog from 'src/modules/admin/order/CollectionDialog'
import OpenTabMergeDialog from 'src/modules/admin/order/components/OpenTabMergeDialog'
import OrderItemDetails from 'src/modules/admin/order/components/OrderItemDetails'
import OrderItemTypeCell from 'src/modules/admin/order/components/OrderItemTypeCell'
import OrderStateCell from 'src/modules/admin/order/components/OrderStateCell'
import SendSmsReceiptDialog from 'src/modules/admin/order/components/SendSmsReceiptDialog'
import CustomTemplateDialog from 'src/modules/admin/order/CustomTemplateDialog'
import EmailItemsDialog from 'src/modules/admin/order/EmailItemsDialog'
import InvoiceDialog from 'src/modules/admin/order/InvoiceDialog'
import OrderItemForm from 'src/modules/admin/order/OrderItemForm'
import OrderReconciliationDialog from 'src/modules/admin/order/OrderReconciliationDialog'
import PrintItemsDialog from 'src/modules/admin/order/PrintItemsDialog'
import ReceiptDialog from 'src/modules/admin/order/ReceiptDialog'
import RefundDialog from 'src/modules/admin/order/RefundDialog'
import SwapDialog from 'src/modules/admin/order/SwapDialog'
import PaymentForm from 'src/modules/admin/payment/PaymentForm'
import OptionsDialog from 'src/modules/register/dialogs/OptionsDialog'

export default {
  name: 'OrderForm',
  components: {
    ModelForm,
    ModelTable,
    EmailItemsDialog,
    PrintItemsDialog,
    RefundDialog,
    SwapDialog,
    ReceiptDialog,
    InvoiceDialog,
    PaymentForm,
    CollectionDialog,
    OpenTabMergeDialog,
    OptionsDialog,
    NotesDialog,
    CustomTemplateDialog,
    OrderItemForm,
    OrderReconciliationDialog,
    SendSmsReceiptDialog
  },
  context: ['currentUser', 'currentRegisterClient'],
  configuration: ['currentCircuit', 'currentSite', 'publishedPrintTemplates'],
  emits: ['change', 'select-order', 'request-authorization', 'force-update'],
  data: function () {
    return {
      tab: 'orders',
      showReceipt: false,
      showInvoice: false,
      showCustomTemplate: false,
      printing: false,
      emailing: false,
      showEmailDialog: false,
      emailAddress: '',
      emailCallback: null,
      cancelingOrder: false,
      openingOrder: false,
      closingOrder: false,
      printEmailView: null,
      showAuditCheckMenu: false,
      showCorrectionsMenu: false,
      runningAuditChecks: false,
      showNotesDialog: false,
      showReconciliationDialog: false
    }
  },
  models: {
    order: {
      skipWatchers: true,
      orderItems: {
        itemOrderItem: {
          itemVariation: true,
          itemModifiers: true,
          item: true
        },
        ticket: {
          ticketType: true,
          user: 'default'
        },
        voucherPurchase: {
          voucherType: true,
          card: 'default'
        },
        tax: true,
        discount: true,
        voucher: 'default',
        voucherType: true,
        giftCard: true,
        membershipInterval: true,
        activityTicket: true,

        canVoid: 'default',
        cannotVoidReason: 'default',
        collectedUser: {
          fields: ['id', 'firstName', 'lastName']
        },
        orderItemBookingFee: true
      },
      payments: {
        giftCard: 'default'
      },
      appliedRewards: true,
      site: true,
      user: {
        userClass: true,
        membership: {
          members: {
            userClass: true,
            membershipType: {
              membershipVouchers: {
                voucherType: true
              },
              membershipDiscounts: {
                discount: true
              }
            },
            vouchers: {
              voucherType: true
            }
          }
        },
        membershipType: {
          membershipVouchers: {
            voucherType: true
          },
          membershipDiscounts: {
            discount: true
          }
        },
        vouchers: {
          voucherType: true
        },
        credit: 'default'
      },
      independentVouchers: {
        voucherType: true
      },
      giftCards: 'default',
      registerStation: true,
      auditChecks: true,
      availableCorrections: true,
      accountingDatesLocked: true,
      notes: true,
      action: true
    }
  },
  computed: {
    availablePrintTemplates () {
      // { label, value, color, callback }
      let availablePrintTemplates = [{ label: 'Receipt', value: 'receipt', color: 'primary', callback: this.handleReceipt }]

      if (this.order.invoicedOrder) {
        availablePrintTemplates.push({ label: 'Invoice', value: 'invoice', color: 'warning', callback: this.handleInvoice })
      }

      if (this.$register && this.currentRegisterClient?.registerStation?.registerType) {
        availablePrintTemplates = _.concat(availablePrintTemplates, _.compact(_.map(this.currentRegisterClient?.registerStation?.registerType.printTemplates, printTemplateId => {
          const printTemplate = _.find(this.publishedPrintTemplates.data, printTemplate => printTemplate.id === printTemplateId)
          if (printTemplate) {
            return { label: printTemplate.name, value: printTemplate, color: 'grey', callback: this.handleCustomTemplate }
          } else {
            return null
          }
        })))
      } else if (this.$admin) {
        availablePrintTemplates = _.concat(availablePrintTemplates, _.map(this.publishedPrintTemplates.data, printTemplate => {
          return { label: printTemplate.name, value: printTemplate, color: 'grey', callback: this.handleCustomTemplate }
        }))
      }

      return availablePrintTemplates
    }
  },
  created () {
    _.extend(this, {
      email,
      required,
      OrderItemTypeCell,
      OrderStateCell,
      OrderItemDetails
    })
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    printOrder () {
      this.printEmailView = 'print'
      this.$refs.optionsDialog.show(this.availablePrintTemplates, 'Print Order')
    },
    emailOrder () {
      this.printEmailView = 'email'
      this.emailCallback = null
      this.$refs.optionsDialog.show(this.availablePrintTemplates, 'Email Order')
    },
    viewOrder () {
      this.printEmailView = 'view'
      this.$refs.optionsDialog.show(this.availablePrintTemplates, 'View Order')
    },
    showEmail () {
      this.emailAddress = this.order?.user?.email || ''
      this.showEmailDialog = true
    },
    async handleReceipt () {
      if (this.printEmailView === 'print') {
        await this.printReceipt()
      } else if (this.printEmailView === 'email') {
        this.emailCallback = this.emailReceipt
        this.showEmail()
      } else if (this.printEmailView === 'view') {
        this.$refs.receiptDialog.show(this.order.id)
      }
    },
    async printReceipt () {
      this.printing = true
      await this.order.printOrderReceipt()
      this.printing = false
    },
    async emailReceipt () {
      if (!this.emailAddress || !this.emailAddress.length) {
        this.showEmailDialog = false
        return
      }
      this.emailing = true
      await this.order.emailOrderReceipt(this.emailAddress)
      this.emailing = false
      this.showEmailDialog = false
    },
    async handleInvoice () {
      if (this.printEmailView === 'print') {
        await this.printInvoice()
      } else if (this.printEmailView === 'email') {
        this.emailCallback = this.emailInvoice
        this.showEmail()
      } else if (this.printEmailView === 'view') {
        this.$refs.invoiceDialog.show(this.order.id)
      }
    },
    async printInvoice () {
      this.printing = true
      await this.order.printOrderInvoice()
      this.printing = false
    },
    async emailInvoice () {
      if (!this.emailAddress || !this.emailAddress.length) {
        this.showEmailDialog = false
        return
      }
      this.emailing = true
      await this.order.emailOrderInvoice(this.emailAddress)
      this.emailing = false
      this.showEmailDialog = false
    },
    async handleCustomTemplate (printTemplate) {
      this.order.printTemplateId = printTemplate.id

      if (this.printEmailView === 'print') {
        await this.printCustomTemplate()
      } else if (this.printEmailView === 'email') {
        this.emailCallback = this.emailCustomTemplate
        this.showEmail()
      } else if (this.printEmailView === 'view') {
        this.$refs.customTemplateDialog.show(this.order.id, printTemplate)
      }
    },
    async printCustomTemplate () {
      this.printing = true
      await this.order.printOrderCustomTemplate()
      this.printing = false
    },
    async emailCustomTemplate () {
      if (!this.emailAddress || !this.emailAddress.length) {
        this.showEmailDialog = false
        return
      }
      this.emailing = true
      await this.order.emailOrderCustomTemplate(this.emailAddress)
      this.emailing = false
      this.showEmailDialog = false
    },
    handleItems () {
      let requireAdminAuth = true
      if (this.$register) {
        requireAdminAuth = this.currentRegisterClient.registerStation.registerType.requireAuthorizationForPrintingItems
      }

      if (this.printEmailView === 'print') {
        if (this.currentUser.admin || !requireAdminAuth) {
          this.printItems()
        } else {
          this.$emit('request-authorization', () => { this.printItems() })
        }
      } else if (this.printEmailView === 'email') {
        if (this.currentUser.admin || !requireAdminAuth) {
          this.emailCallback = this.emailItems
          this.showEmail()
        } else {
          this.$emit('request-authorization', () => { this.emailCallback = this.emailItems; this.showEmail() })
        }
      }
    },
    printItems () {
      this.printing = true
      this.$refs.printItemsDialog.show(this.order)
      this.printing = false
    },
    sendSmsReceipt () {
      this.$refs.sendSmsReceiptDialog.show(this.order)
    },
    emailItems () {
      if (!this.emailAddress || !this.emailAddress.length) {
        this.showEmailDialog = false
        return
      }
      this.emailing = true
      this.$refs.emailItemsDialog.show(this.order, this.emailAddress)
      this.emailing = false
      this.showEmailDialog = false
    },
    async cancelOrder () {
      this.cancelingOrder = true
      await this.order.cancel()
      this.cancelingOrder = false
    },
    async closeOrder () {
      this.closingOrder = true
      await this.order.place()
      this.closingOrder = false
    },
    async runAuditChecks () {
      this.runningAuditChecks = true
      await this.order.runAuditChecks()
      this.runningAuditChecks = false
    },
    refund () {
      if (this.currentUser.admin) {
        this.$refs.refundDialog.show(this.order)
      } else {
        this.$emit('request-authorization', (authIdentifier) => {
          this.order.addNote(`Action - Refund Order - ${authIdentifier}`)
          this.$refs.refundDialog.show(this.order)
        })
      }
    },
    merge () {
      if (this.currentUser.admin) {
        this.$refs.openTabMergeDialog.show(this.order)
      } else {
        this.$emit('request-authorization', () => { this.$refs.openTabMergeDialog.show(this.order) })
      }
    },
    async mergeOrder (order) {
      this.order.action = 'merge_tab_order'
      this.order.mergeTabOrderId = order.id
      const result = await this.order.save()

      if (result.errors.length) {
        this.$mh.showErrors(result.errors)
      }

      this.order.action = null
      this.order.mergeTabOrderId = null
      this.$emit('force-update')
    },
    swap () {
      if (this.currentUser.admin) {
        this.$refs.swapDialog.show()
      } else {
        this.$emit('request-authorization', () => { this.$refs.swapDialog.show() })
      }
    },
    classesForItem (orderItem) {
      if (orderItem.state === 'placed') {
        return []
      } else if (orderItem.state === 'paid') {
        return []
      } else if (orderItem.state === 'voided') {
        return ['bg-red-1']
      } else if (orderItem.state === 'draft') {
        return ['bg-grey-4']
      } else if (orderItem.state === 'canceled') {
        return ['bg-red-1']
      } else {
        throw new Error('Unhandled Condition: ' + orderItem.state)
      }
    },
    classesForPayment (payment) {
      if (payment.state === 'completed') {
        if (payment.amount < 0) {
          return ['bg-red-1']
        } else {
          return []
        }
      } else if (payment.state === 'canceled') {
        return ['bg-red-1']
      } else if (payment.state === 'draft') {
        return ['bg-grey-4']
      } else if (payment.state === 'pending' || payment.state === 'pre_auth') {
        return ['bg-yellow-4']
      } else if (payment.state === 'disputed' || payment.state === 'lost_dispute') {
        return ['bg-orange-4']
      } else {
        throw new Error('Unhandled Condition: ' + payment.state)
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .poster-img {
    width: 60px;
    height: 80px;
  }
  .banner-img {
    width: 100%;
  }
</style>
